import type {
  MagentoAddToCartContext,
  MagentoCartItemInput,
  MagentoConfigurableProductCartItemInput,
  MagentoSimpleProductCartItemInput,
} from '~/app/types/magento'
import addProductsToCart from '~layers/app/graphql/cart/addProductsToCart.gql'
import TwSuggestionsQuery from '~layers/app/graphql/pdp/twSuggestions.gql'

export function useAddToCart() {
  const { addToCart: addToCartDatalayer } = useDataLayer()
  const { sessionKey, profileKey, track: trackTweakwise } = useTweakwise()
  const { items, errors, pending, getCart, createEmptyCart, getCartId, sumQuantity } = useCart()
  const addToCartContext = useState<MagentoAddToCartContext | undefined>('addToCartContext', () => undefined)
  const totalQuantity = useState<number>('cartTotalQuantity', () => 0)

  watch (sumQuantity, (newValue) => {
    totalQuantity.value = newValue
  })

  const {
    mutate: addProductsToCartMutation,
    error: addProductsToCartError,
  } = useMutation(
    addProductsToCart,
    { clientId: 'customer' },
  )

  const addToCart = async (cartItems: (MagentoCartItemInput | MagentoConfigurableProductCartItemInput | MagentoSimpleProductCartItemInput)[], context: MagentoAddToCartContext) => {
    pending.value = true
    if (!getCartId.value) {
      await createEmptyCart()
    }
    if (!getCartId.value) {
      return
    }

    context.items = cartItems.map((item) => {
      if (item.data) {
        return {
          quantity: item.data.quantity,
          sku: item.data.sku,
          parent_sku: item.parent_sku || undefined,
          entered_options: item.customizable_options?.map((item: any) => {
            return {
              uid: btoa(`custom-option/${item.id}`),
              value: item.value_string,
            }
          }) || undefined,
        } as MagentoCartItemInput
      }
      return item as MagentoCartItemInput
    })

    try {
      const { client } = useApolloClient()

      const [result, crossSells] = await Promise.all([
        addProductsToCartMutation({
          cartId: getCartId.value,
          cartItems: context.items,
        }),
        client.query<any>({
          query: TwSuggestionsQuery,
          variables: {
            twItemId: context.product?.tweakwiseId || '0',
            profileKey: profileKey.value,
            groupKey: 'cross-sell',
          },
        }),
      ])

      errors.value = []
      if (result?.data?.addProductsToCart?.user_errors) {
        errors.value = result.data.addProductsToCart.user_errors.map((error: { message: string }) => error.message)
      }
      pending.value = false
      if (result?.data?.addProductsToCart?.cart) {
        addToCartContext.value = {
          ...context,
          upsell: crossSells.data?.twRecommendations?.recommendations?.reduce((acc: any[], item: any) => {
            return [
              ...acc,
              ...item?.items || [],
            ]
          }, []) || [],
        }
        totalQuantity.value = result.data.addProductsToCart.cart.total_quantity || 0
        getCart({ force: true }).then(() => {
          const addedItems = items.value?.filter(item => item.product.sku === context.product.sku) || []
          addToCartDatalayer(addedItems)

          addedItems.forEach((item) => {
            trackTweakwise('addtocart', {
              ProfileKey: profileKey.value,
              SessionKey: sessionKey.value,
              ProductKey: item.product.product.tweakwiseId,
              Quantity: item.quantity,
              TotalAmount: item.prices?.row_total_including_tax?.value || 0,
            })
          })
        })
      }
    }
    catch {
      if (addProductsToCartError.value?.graphQLErrors) {
        errors.value = addProductsToCartError.value?.graphQLErrors.map(item => item.message)
      }
      pending.value = false
    }
  }

  return {
    addToCartContext,
    addToCart,
    totalQuantity,
  }
}
